import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/layout/Layout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../components/ui/Table';
import { Plus, Ban, Copy, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  listCoupons,
  createCoupon,
  disableCoupon,
  type Coupon,
  type CreateCouponRequest,
} from '../../modules/billing/api/billing.client';
import { showErrorToast, showSuccessToast } from '../../utils/errorHandler';

const LIMIT_OPTIONS = [10, 20, 50].map((n) => ({ value: String(n), label: String(n) }));

function formatValue(coupon: Coupon): string {
  if (coupon.type === 'percent') return `${coupon.value}%`;
  const curr = (coupon.currency ?? 'usd').toUpperCase();
  const dollars = (coupon.value / 100).toFixed(2);
  return `${curr} ${dollars}`;
}

function formatDuration(coupon: Coupon): string {
  if (coupon.duration === 'once') return 'Once';
  if (coupon.duration === 'forever') return 'Forever';
  const months = coupon.duration_in_months ?? 0;
  return `Repeating (${months} mo)`;
}

function formatCreated(createdAt?: string): string {
  if (!createdAt) return '—';
  try {
    return new Date(createdAt).toLocaleDateString();
  } catch {
    return '—';
  }
}

export const DevCouponsPage: React.FC = () => {
  const [items, setItems] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [limit, setLimit] = useState(20);
  const [skip, setSkip] = useState(0);
  const [searchCode, setSearchCode] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [disableTarget, setDisableTarget] = useState<Coupon | null>(null);

  const refetch = useCallback(() => {
    setLoading(true);
    setError(null);
    const statusParam = statusFilter === 'all' ? undefined : statusFilter;
    listCoupons({ status: statusParam, limit, skip })
      .then(setItems)
      .catch((e) => {
        setError(e instanceof Error ? e.message : 'Failed to load coupons');
        showErrorToast(e);
      })
      .finally(() => setLoading(false));
  }, [statusFilter, limit, skip]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const handleDisable = async (coupon: Coupon) => {
    setDisableTarget(null);
    try {
      await disableCoupon(coupon.coupon_id);
      showSuccessToast('Coupon disabled');
      refetch();
    } catch (e) {
      showErrorToast(e);
    }
  };

  const copyCode = (code: string) => {
    navigator.clipboard?.writeText(code).then(
      () => showSuccessToast('Code copied'),
      () => showErrorToast('Copy failed')
    );
  };

  const filteredItems = searchCode.trim()
    ? items.filter((c) => c.code.toLowerCase().includes(searchCode.trim().toLowerCase()))
    : items;

  return (
    <Layout>
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-xl font-semibold text-app-text dark:text-white">Coupons</h1>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create coupon
          </Button>
        </div>

        <Card padding="md">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <div className="flex-1 min-w-[160px]">
                  <input
                    type="text"
                    placeholder="Search by code"
                    value={searchCode}
                    onChange={(e) => setSearchCode(e.target.value)}
                    className="w-full rounded-lg border border-app-border dark:border-gray-600 bg-app-surface dark:bg-gray-800 px-3 py-2 text-sm text-app-text dark:text-white placeholder:text-app-text-muted dark:placeholder:text-gray-500"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-app-text-muted dark:text-gray-400">Status</span>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
                    className="rounded-lg border border-app-border dark:border-gray-600 bg-app-surface dark:bg-gray-800 px-3 py-2 text-sm text-app-text dark:text-white"
                  >
                    <option value="all">All</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-app-text-muted dark:text-gray-400">Limit</span>
                  <select
                    value={String(limit)}
                    onChange={(e) => setLimit(Number(e.target.value))}
                    className="rounded-lg border border-app-border dark:border-gray-600 bg-app-surface dark:bg-gray-800 px-3 py-2 text-sm text-app-text dark:text-white"
                  >
                    {LIMIT_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-2 ml-auto">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={skip === 0}
                    onClick={() => setSkip((s) => Math.max(0, s - limit))}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-app-text-muted dark:text-gray-400">
                    {skip + 1}–{skip + items.length}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={items.length < limit}
                    onClick={() => setSkip((s) => s + limit)}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {error && (
                <div className="mb-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-300">
                  {error}
                </div>
              )}

              {loading ? (
                <div className="flex justify-center py-12">
                  <Spinner size="lg" />
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="py-12 text-center text-app-text-muted dark:text-gray-400">
                  <p>{searchCode.trim() ? 'No coupons match your search.' : 'No coupons yet. Create one.'}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Value</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredItems.map((coupon) => (
                      <TableRow key={coupon.coupon_id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-medium">{coupon.code}</span>
                            <button
                              type="button"
                              onClick={() => copyCode(coupon.code)}
                              className="p-1 rounded hover:bg-app-surface-muted dark:hover:bg-gray-700 text-app-text-muted hover:text-app-text"
                              aria-label="Copy code"
                            >
                              <Copy className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </TableCell>
                        <TableCell className="capitalize">{coupon.type}</TableCell>
                        <TableCell>{formatValue(coupon)}</TableCell>
                        <TableCell>{formatDuration(coupon)}</TableCell>
                        <TableCell>
                          <Badge variant={coupon.status === 'active' ? 'success' : 'gray'}>
                            {coupon.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatCreated(coupon.created_at)}</TableCell>
                        <TableCell className="text-right">
                          {coupon.status === 'active' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDisableTarget(coupon)}
                              className="text-error-600 dark:text-error-400"
                            >
                              <Ban className="w-4 h-4 mr-1" />
                              Disable
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
        </Card>
      </div>

      {/* Disable confirm */}
      <Modal
        isOpen={!!disableTarget}
        onClose={() => setDisableTarget(null)}
        title="Disable coupon"
      >
        {disableTarget && (
          <div className="space-y-4">
            <p className="text-app-text dark:text-gray-200">
              Disable coupon <strong className="font-mono">{disableTarget.code}</strong>? It will no longer be redeemable.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDisableTarget(null)}>
                Cancel
              </Button>
              <Button variant="danger" onClick={() => handleDisable(disableTarget)}>
                Disable
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Create coupon */}
      <CreateCouponModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={() => {
          setCreateOpen(false);
          showSuccessToast('Coupon created');
          refetch();
        }}
      />
    </Layout>
  );
};

interface CreateCouponModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function CreateCouponModal({ isOpen, onClose, onSuccess }: CreateCouponModalProps) {
  const [code, setCode] = useState('');
  const [type, setType] = useState<'percent' | 'amount'>('percent');
  const [value, setValue] = useState('');
  const [duration, setDuration] = useState<'once' | 'repeating' | 'forever'>('once');
  const [durationInMonths, setDurationInMonths] = useState('');
  const [currency, setCurrency] = useState('usd');
  const [maxRedemptions, setMaxRedemptions] = useState('');
  const [redeemByDate, setRedeemByDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!code.trim()) e.code = 'Code is required';
    const num = type === 'percent' ? Number(value) : Number(value);
    if (Number.isNaN(num)) e.value = 'Value must be a number';
    else if (type === 'percent' && (num < 1 || num > 100)) e.value = 'Percent must be 1–100';
    else if (type === 'amount' && num < 1) e.value = 'Amount must be at least 1 (cents)';
    if (duration === 'repeating') {
      const m = Number(durationInMonths);
      if (Number.isNaN(m) || m < 1) e.duration_in_months = 'Months must be ≥ 1';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload: CreateCouponRequest = {
        code: code.trim().toUpperCase(),
        type,
        value: type === 'percent' ? Number(value) : Math.round(Number(value)),
        duration,
        currency: type === 'amount' ? currency : undefined,
        max_redemptions: maxRedemptions.trim() ? Number(maxRedemptions) : undefined,
        redeem_by: redeemByDate.trim() ? Math.floor(new Date(redeemByDate).getTime() / 1000) : undefined,
      };
      if (duration === 'repeating' && durationInMonths.trim()) {
        payload.duration_in_months = Number(durationInMonths);
      }
      await createCoupon(payload);
      onSuccess();
      setCode('');
      setValue('');
      setDurationInMonths('');
      setMaxRedemptions('');
      setRedeemByDate('');
    } catch (err) {
      showErrorToast(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create coupon" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Code"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="e.g. OFF20"
          error={errors.code}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-app-text-muted dark:text-gray-300 mb-1">Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as 'percent' | 'amount')}
              className="w-full rounded-lg border border-app-border dark:border-gray-600 bg-app-surface dark:bg-gray-800 px-4 py-2 text-app-text dark:text-white"
            >
              <option value="percent">Percent</option>
              <option value="amount">Amount (cents)</option>
            </select>
          </div>
          <Input
            label={type === 'percent' ? 'Value (%)' : 'Value (cents)'}
            type="number"
            min={type === 'percent' ? 1 : 1}
            max={type === 'percent' ? 100 : undefined}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            error={errors.value}
          />
        </div>
        {type === 'amount' && (
          <div>
            <label className="block text-sm font-medium text-app-text-muted dark:text-gray-300 mb-1">Currency</label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="w-full rounded-lg border border-app-border dark:border-gray-600 bg-app-surface dark:bg-gray-800 px-4 py-2 text-app-text dark:text-white"
            >
              <option value="usd">USD</option>
              <option value="inr">INR</option>
            </select>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-app-text-muted dark:text-gray-300 mb-1">Duration</label>
            <select
              value={duration}
              onChange={(e) => setDuration(e.target.value as 'once' | 'repeating' | 'forever')}
              className="w-full rounded-lg border border-app-border dark:border-gray-600 bg-app-surface dark:bg-gray-800 px-4 py-2 text-app-text dark:text-white"
            >
              <option value="once">Once</option>
              <option value="repeating">Repeating</option>
              <option value="forever">Forever</option>
            </select>
          </div>
          {duration === 'repeating' && (
            <Input
              label="Duration (months)"
              type="number"
              min={1}
              value={durationInMonths}
              onChange={(e) => setDurationInMonths(e.target.value)}
              error={errors.duration_in_months}
            />
          )}
        </div>
        <Input
          label="Max redemptions (optional)"
          type="number"
          min={1}
          value={maxRedemptions}
          onChange={(e) => setMaxRedemptions(e.target.value)}
        />
        <Input
          label="Redeem by date (optional)"
          type="date"
          value={redeemByDate}
          onChange={(e) => setRedeemByDate(e.target.value)}
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? <Spinner size="sm" className="mr-2" /> : null}
            Create
          </Button>
        </div>
      </form>
    </Modal>
  );
}
